// src/components/ControlContratistas.js
import React, { useState, useEffect, useMemo } from 'react';
import axios from "./../services/http";
import { Typography, Table, Button, Spin, message } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { FileExcelOutlined } from '@ant-design/icons';
import config from "./../config";
import DownloadFile from '../utils/DownloadFile';

const { Title } = Typography;

const ControlContratistas = ({ obraId, goBack }) => {

  const location = useLocation();
  const from = location.state?.from || null;
  const navigate = useNavigate();
  const [controlesPago, setControlesPago] = useState([]);
  const [loading, setLoading] = useState(true);
  const numberFormat = new Intl.NumberFormat('es-MX', {minimumFractionDigits: 2});

  useEffect(() => {
    const fetchControlesDePago = async () => {
      try {
        // Use obraId prop if available, otherwise use from.id
        const id = obraId || from?.id;

        if (!id) {
          console.error('No obra ID available');
          setLoading(false);
          return;
        }

        const response = await axios.HTTP_GET(`/api/construction/cp/${id}`);
        for(let i = 0; i < response.length; i++) {
          response[i]["total_nofiscal"] = 0;
          response[i]["total_fiscal"] = 0;
        }
        setLoading(false);

        const responseTotal = await axios.HTTP_POST("/api/construction/count/amounts", { obraId: id });
        if (responseTotal && responseTotal.data.length > 0) {
          for(let i = 0; i < response.length; i++) {
            let relation = responseTotal.data.filter(item => item.partida === response[i].partidas && item.control_pago_id === response[i].id);
            if (relation.length > 0) {
              let noFiscal = relation.filter(item => item["tipo_monto"] === "No Fiscal");
              let fiscal = relation.filter(item => item["tipo_monto"] === "Fiscal");
              if (fiscal.length > 0) response[i]["total_fiscal"] = fiscal[0].Total;
              if (noFiscal.length > 0) response[i]["total_nofiscal"] = noFiscal[0].Total;
            }
          }
        }
        response.sort((a, b) => {
          const idA = (a.partidas || '').toString();
          const idB = (b.partidas || '').toString();
          return idA.localeCompare(idB, undefined, { numeric: true });
        });
        setControlesPago(response);
      } catch (error) {
        console.error('Error al cargar los controles de pago:', error);
        setLoading(false);
      }
    };

    fetchControlesDePago();
  }, [obraId, from]);

  const GenerateExcel = async () => {
    try {
      setLoading(true);
      const response = await axios.HTTP_POST("/api/v1/builders/excel", controlesPago);
      if (response && response.data) {
        setTimeout(() => DownloadFile((response !== null ? `${config.server}/${response.data.pathFile}` : null), (response !== null && response.data !== undefined ? response.data.name : null)), 1000);
        setTimeout(() => DeleteFile(response.data.pathFile), 2000);
        
      }
    } catch (e) {
      console.error('Error al generar reporte:', e);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }

  const DeleteFile = async (document) => {
    try {
        const response = await axios.HTTP_POST("/api/v1/extras/delete-document", { path: document });
        if (response && response.data && response.data.success) console.log("delete file ok");
    } catch (e) {
        console.error('Error al eliminar el archivo:', e);
        message.error(e);
    }
}

  const totals = useMemo(() => {
    let sumPresupuesto = 0, sumContratoFiscal = 0, sumContratoNoFiscal = 0;
    let sumRegFiscal = 0, sumRegNoFiscal = 0, sumRestFiscal = 0, sumRestNoFiscal = 0, sumTotalRestante = 0;
    controlesPago.forEach((record) => {
      let { presupuesto, presupuesto_iva, presupuesto_no_iva, total_fiscal, total_nofiscal } = record;
      presupuesto = presupuesto || 0;
      let budgetIva = presupuesto * ((presupuesto_iva || 0) / 100);
      let budgetNotIva = presupuesto * ((presupuesto_no_iva || 0) / 100);
      let rFiscal = budgetIva - (total_fiscal || 0);
      let rNoFiscal = budgetNotIva - (total_nofiscal || 0);
      sumPresupuesto += presupuesto;
      sumContratoFiscal += budgetIva;
      sumContratoNoFiscal += budgetNotIva;
      sumRegFiscal += (total_fiscal || 0);
      sumRegNoFiscal += (total_nofiscal || 0);
      sumRestFiscal += rFiscal;
      sumRestNoFiscal += rNoFiscal;
      sumTotalRestante += rFiscal + rNoFiscal;
    });
    return { sumPresupuesto, sumContratoFiscal, sumContratoNoFiscal, sumRegFiscal, sumRegNoFiscal, sumRestFiscal, sumRestNoFiscal, sumTotalRestante };
  }, [controlesPago]);

  if (loading)
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <Spin size="large" />
        <Typography>Cargando...</Typography>
      </div>
    );

  const columns = [
    {
      title: 'ID',
      dataIndex: 'partidas',
      key: 'id',
      render: (text) => text || 'ID Desconocido',
    },
    {
      title: 'CONTROL PAGOS',
      key: 'controlPagos',
      render: (record) => {
        let url = `${record.nombre_obra || 'Obra Desconocida'} / ${record.nombre_contratista || 'Contratista Desconocido'} / ${record.concepto || 'Contratista Descrip. Desconocido'}`;
        return <a onClick={() => navigate("/payment-control-list", { state: { from: from || { id: obraId }, contratista_id: record.contratista_id, cp: record.id } })}> {url} </a>
      }
    },
    {
      title: 'TOTAL',
      dataIndex: 'presupuesto',
      key: 'total',
      render: (text) =>  {
        return `$ ${numberFormat.format(text || 0)}`
      }
    },
    {
      title: 'IVA',
      dataIndex: 'iva',
      key: 'iva',
      render: (text) => `${parseFloat(text || 0)}%`,
    },
    {
      title: 'CONTRATO FISCAL',
      key: 'contratoFiscal',
      render: (record) => {
        let { presupuesto, presupuesto_iva } = record;
        const budgetIva = presupuesto * (presupuesto_iva/100);

        return `$ ${numberFormat.format(budgetIva)}`;
      },
    },
    {
      title: 'CONTRATO NO FISCAL',
      key: 'contratoNoFiscal',
      render: (record) => {
        let { presupuesto, presupuesto_no_iva } = record;
        const budgetNotIva = presupuesto * (presupuesto_no_iva / 100);
        
        return `$ ${numberFormat.format(budgetNotIva)}`;
      }
    },
    {
      title: 'REGISTRADO FISCAL',
      dataIndex: 'total_fiscal',
      key: 'registradoFiscal',
      render: (record) => `$ ${numberFormat.format(record || 0)}`
    },
    {
      title: 'REGISTRADO NO FISCAL',
      dataIndex: 'total_nofiscal',
      key: 'registradoNoFiscal',
      render: (record) => `$ ${numberFormat.format(record || 0)}`
    },
    {
      title: 'RESTANTE FISCAL',
      key: 'restanteFiscal',
      render: (record) => {
        let { presupuesto, presupuesto_iva } = record;
        const budgetIva = presupuesto * (presupuesto_iva/100);

        return `$ ${numberFormat.format(budgetIva - record.total_fiscal)}`;
      },
    },
    {
      title: 'RESTANTE NO FISCAL',
      key: 'restanteNoFiscal',
      render: (record) => {
        let { presupuesto, presupuesto_no_iva } = record;
        const budgetNotIva = presupuesto * (presupuesto_no_iva / 100);
        
        return `$ ${numberFormat.format(budgetNotIva - record.total_nofiscal)}`;
      },
    },
    {
      title: 'TOTAL RESTANTE',
      key: 'totalRestante',
      render: (record) => {
        let { presupuesto, presupuesto_iva, presupuesto_no_iva, total_fiscal, total_nofiscal } = record;
        const budgetIva = presupuesto * (presupuesto_iva/100);
        let remainingIva = (budgetIva - total_fiscal);

        const budgetNotIva = presupuesto * (presupuesto_no_iva / 100);
        let remainingNotIva = (budgetNotIva - total_nofiscal);
        
        return `$ ${numberFormat.format(remainingIva+remainingNotIva)}`;
      },
    },
  ];

  const Navigate = (path, params) => {
    navigate(path, params);
  }

  return (
    <div style={{ marginTop: '32px' }}>
      <div style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
        <Title level={4}>Control de Contratistas</Title>
        <Button color="default" variant="solid" onClick={() => GenerateExcel()}>
          <FileExcelOutlined /> Descargar
        </Button>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '12px 16px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 4, marginBottom: 8, fontWeight: 'bold', fontSize: 13 }}>
        <span>TOTALES:</span>
        <span>Total: $ {numberFormat.format(totals.sumPresupuesto)}</span>
        <span>Contrato Fiscal: $ {numberFormat.format(totals.sumContratoFiscal)}</span>
        <span>Contrato No Fiscal: $ {numberFormat.format(totals.sumContratoNoFiscal)}</span>
        <span>Reg. Fiscal: $ {numberFormat.format(totals.sumRegFiscal)}</span>
        <span>Reg. No Fiscal: $ {numberFormat.format(totals.sumRegNoFiscal)}</span>
        <span>Rest. Fiscal: $ {numberFormat.format(totals.sumRestFiscal)}</span>
        <span>Rest. No Fiscal: $ {numberFormat.format(totals.sumRestNoFiscal)}</span>
        <span>Total Restante: $ {numberFormat.format(totals.sumTotalRestante)}</span>
      </div>
      <Table
        dataSource={controlesPago}
        columns={columns}
        rowKey={record => record.id}
        pagination={false}
      />
    </div>
  );
};

export default ControlContratistas;
